import React from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Fab,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CloseIcon from "@mui/icons-material/Close";
import { useTarget } from "../contexts/TargetContext";
import { useDialog } from "../contexts/DialogContext";
import ImportDialog from "./ImportDialog";
import EditDialog from "./EditDialog";
import { ConfirmDialog } from "./ConfirmDialog";
import DetailView from "./DetailView";
import { getFilterDisplay } from "../utils/getFilterDisplay";
import { useUI } from "../contexts/UIContext";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";

// Add a helper to determine if a color is light
function isColorLight(hex: string): boolean {
  // Remove # if present
  hex = hex.replace("#", "");
  // Convert 3-digit to 6-digit
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((x) => x + x)
      .join("");
  }
  if (hex.length !== 6) return false;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Perceived brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 200;
}

function formatExposureHM(sec: number) {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

const MainContentModern: React.FC = () => {
  const {
    targets,
    loading,
    error,
    selectedTarget,
    setSelectedTarget,
    handleDelete,
    deleteTargetById,
    search,
    setSearch,
  } = useTarget();

  // Use UI context for filter settings
  const { filterSettings, filterSettingsLoading } = useUI();

  // Use dialog state from DialogContext
  const {
    setImportDialogOpen,
    setEditDialogOpen,
    setDeleteType,
    setConfirmDialogOpen,
    setPendingDeleteTargetId,
    setPendingEditTargetId,
  } = useDialog();

  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuTargetId, setMenuTargetId] = React.useState<string | null>(null);
  const [deleteMode, setDeleteMode] = React.useState(false);
  const [selectedForDelete, setSelectedForDelete] = React.useState<Set<string>>(
    new Set()
  );
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [selectedTab, setSelectedTab] = React.useState<
    "overview" | "frame" | "quality" | "guiding"
  >("overview");
  const [frameTypeFilter, setFrameTypeFilter] = React.useState("All");
  const [filterTypeFilter, setFilterTypeFilter] = React.useState("All");
  const [selectedNight, setSelectedNight] = React.useState("total");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [targetLastSelectedNight, setTargetLastSelectedNight] = React.useState<
    Record<string, string>
  >({}); // Currently unused but needed for DetailView component prop
  const confirmDialogRef = React.useRef<any>(null);

  // Filter targets by search
  const filteredTargets = targets.filter((t: any) => {
    const searchLower = search.toLowerCase();
    // Search by target name
    if (t.name.toLowerCase().includes(searchLower)) return true;

    // Search by frames if frames exist
    if (t.images && t.images.length > 0) {
      // Search by frame type
      const hasMatchingFrameType = t.images.some(
        (img: any) =>
          img.FrameType && img.FrameType.toLowerCase().includes(searchLower)
      );
      if (hasMatchingFrameType) return true;

      // Search by filter name
      const hasMatchingFilter = t.images.some(
        (img: any) =>
          img.FilterName && img.FilterName.toLowerCase().includes(searchLower)
      );
      if (hasMatchingFilter) return true;
    }

    return false;
  });

  // Compute stats for a target
  function getTargetStats(target: any) {
    const nights = Object.keys(target.nightMetadata || {});
    const frames = target.images ? target.images.length : 0;
    const exposure = target.totalExposureTime || 0;
    return { nights: nights.length, frames, exposure };
  }

  // Equipment summary
  function getEquipmentSummary(target: any) {
    if (!target.equipment) return "No equipment info";
    const eq = target.equipment;
    return `${eq.telescopeName || ""} (${eq.focalLength || "?"}mm, f/${
      eq.focalRatio || "?"
    }), ${eq.cameraName || ""}`;
  }

  // Delete handler
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteTarget = async (id: string) => {
    if (selectedTarget && selectedTarget.id === id) {
      await handleDelete();
    } else {
      // Select then delete
      setSelectedTarget(targets.find((t: any) => t.id === id) || null);
      setTimeout(() => handleDelete(), 0);
    }
    setMenuAnchor(null);
    setMenuTargetId(null);
  };

  // Delete mode handlers
  const handleToggleDeleteMode = () => {
    setDeleteMode((prev) => !prev);
    setSelectedForDelete(new Set());
  };
  const handleSelectCard = (id: string) => {
    setSelectedForDelete((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const handleDeleteSelected = () => {
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    const ids = Array.from(selectedForDelete);
    // Use the new batch delete functionality
    await deleteTargetById(ids);
    setDeleteMode(false);
    setSelectedForDelete(new Set());
  };

  // Compute night keys and images for the selected view
  const nightKeys = selectedTarget
    ? Object.keys(selectedTarget.nightMetadata || {})
    : [];
  const imagesForView = selectedTarget
    ? selectedNight === "total"
      ? selectedTarget.images
      : selectedTarget.images.filter((img) => {
          // Try to match by night in FilePath or ExposureStart
          const match = img.FilePath.match(/DATE_(\d{4}-\d{2}-\d{2})/);
          const night = match
            ? match[1]
            : (img.ExposureStart || "").slice(0, 10);
          return night === selectedNight;
        })
    : [];

  // Dummy helpers for now (replace with real ones if needed)
  const getNightMeta = () => "—";
  const formatMultipleValues = (values: any) =>
    Array.isArray(values) ? values.join(", ") : values;

  // Don't render anything until filter settings are loaded
  if (filterSettingsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: selectedTarget ? 3 : 8,
        p: 3,
        minHeight: "100vh",
        bgcolor: "background.default",
        position: "relative",
      }}
    >
      {/* Master: Target List */}
      {!selectedTarget && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "background.paper",
                borderRadius: 2,
                px: 2,
                width: 260,
                height: 40,
              }}
            >
              <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              <InputBase
                placeholder="Search targets/frames…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ color: "text.primary", width: "100%" }}
              />
              {search && (
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  edge="end"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => setImportDialogOpen(true)}
            >
              Add Target
            </Button>
            {!deleteMode && (
              <Button
                variant="outlined"
                startIcon={<DeleteSweepIcon />}
                size="small"
                onClick={handleToggleDeleteMode}
                disabled={targets.length === 0}
                color="error"
              >
                Delete Mode
              </Button>
            )}
            {deleteMode && (
              <>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={handleDeleteSelected}
                  disabled={selectedForDelete.size === 0}
                >
                  Delete Selected ({selectedForDelete.size})
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleToggleDeleteMode}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>

          {loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress
                size={60}
                sx={{ color: "primary.main", mb: 3 }}
              />
              <Typography variant="body1" color="text.secondary">
                Loading targets...
              </Typography>
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 4, maxWidth: 800 }}>
              {error}
            </Alert>
          )}

          {!loading && targets.length === 0 && (
            <Box sx={{ textAlign: "center", mt: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No targets found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Click "Add Target" to create your first target.
              </Typography>
              <Fab
                color="primary"
                aria-label="add"
                onClick={() => setImportDialogOpen(true)}
              >
                <AddIcon />
              </Fab>
            </Box>
          )}

          {!loading && targets.length > 0 && (
            <Grid container spacing={3} alignItems="stretch">
              {filteredTargets.map((target: any) => {
                const stats = getTargetStats(target);
                const checked = selectedForDelete.has(target.id);
                return (
                  <Grid
                    key={target.id}
                    size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <Box
                      sx={{
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        p: 2,
                        minHeight: 180,
                        flex: 1,
                        boxShadow: 2,
                        cursor: deleteMode ? "pointer" : "pointer",
                        transition:
                          "box-shadow 0.2s, transform 0.2s, border 0.2s, filter 0.2s",
                        "&:hover": {
                          boxShadow: 6,
                          transform: "translateY(-2px) scale(1.03)",
                          border: deleteMode ? "2px solid #ff6b35" : undefined,
                        },
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        position: "relative",
                        border:
                          deleteMode && checked
                            ? "4px solid #ff3b1f"
                            : deleteMode
                            ? "2px solid #ffb199"
                            : undefined,
                        filter: deleteMode ? "brightness(0.85)" : undefined,
                      }}
                      onClick={() => {
                        if (deleteMode) handleSelectCard(target.id);
                        else setSelectedTarget(target);
                      }}
                    >
                      {/* Card content, faded in delete mode */}
                      <Box
                        sx={{
                          opacity: deleteMode ? 0.5 : 1,
                          transition: "opacity 0.2s",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          {!deleteMode && (
                            <Avatar
                              sx={{ width: 48, height: 48, borderRadius: 2 }}
                            >
                              {target.name[0]}
                            </Avatar>
                          )}
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight={700}>
                              {target.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getEquipmentSummary(target)}
                            </Typography>
                          </Box>
                          {!deleteMode && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuAnchor(e.currentTarget);
                                setMenuTargetId(target.id);
                              }}
                              sx={{ ml: 1 }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 1,
                            color: "text.secondary",
                            fontSize: 14,
                          }}
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(4, 1fr)",
                              gap: 2,
                            }}
                          >
                            {["LIGHT", "DARK", "FLAT", "BIAS"].map((type) => {
                              const count = target.images
                                ? target.images.filter((img: any) =>
                                    img.FrameType
                                      ? img.FrameType === type
                                      : type === "LIGHT" && !!img.FilterName
                                  ).length
                                : 0;
                              return (
                                <span key={type}>
                                  {type}: <b>{count}</b>
                                </span>
                              );
                            })}
                          </Box>
                          <span
                            style={{ textAlign: "center", marginTop: "0.5rem" }}
                          >
                            Exposure: <b>{formatExposureHM(stats.exposure)}</b>
                          </span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {target.filters &&
                            target.filters.map((filter: string) => {
                              const display = getFilterDisplay(
                                filter,
                                filterSettings
                              );
                              if (!display) return null;
                              const { name, color } = display;
                              // Use black text if background is light, else white
                              const textColor = isColorLight(color)
                                ? "#222"
                                : "#fff";
                              return (
                                <Box
                                  key={filter}
                                  sx={{
                                    bgcolor: color,
                                    color: textColor,
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: 1,
                                    fontSize: "0.8em",
                                    fontWeight: 500,
                                    textShadow:
                                      textColor === "#fff"
                                        ? "0 1px 2px rgba(0,0,0,0.25)"
                                        : "none",
                                  }}
                                >
                                  {name}
                                </Box>
                              );
                            })}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
          {/* Card menu */}
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => {
              setMenuAnchor(null);
              setMenuTargetId(null);
            }}
          >
            <MenuItem
              onClick={() => {
                const target =
                  targets.find((t: any) => t.id === menuTargetId) || null;
                setMenuAnchor(null);
                setMenuTargetId(null);
                if (target) {
                  setPendingEditTargetId(target.id);
                  setEditDialogOpen(true);
                }
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                const target =
                  targets.find((t: any) => t.id === menuTargetId) || null;
                setMenuAnchor(null);
                setMenuTargetId(null);
                if (target) {
                  setPendingDeleteTargetId(target.id);
                  setDeleteType("target");
                  setConfirmDialogOpen(true);
                }
              }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </Menu>
          <ImportDialog />
          <EditDialog />
          <ConfirmDialog />
          {/* Confirm Delete Dialog */}
          <Menu anchorEl={null} open={false} onClose={() => {}} />
          {confirmOpen && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                bgcolor: "rgba(0,0,0,0.4)",
                zIndex: 3000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: 3,
                  p: 4,
                  minWidth: 340,
                  boxShadow: 8,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Delete {selectedForDelete.size} Target
                  {selectedForDelete.size > 1 ? "s" : ""}?
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={3}>
                  This action cannot be undone. Are you sure you want to delete
                  the selected target{selectedForDelete.size > 1 ? "s" : ""}?
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  sx={{ mr: 2, minWidth: 100, fontWeight: 700 }}
                  onClick={handleConfirmDelete}
                >
                  Delete
                </Button>
                <Button
                  variant="outlined"
                  sx={{ minWidth: 100, fontWeight: 700 }}
                  onClick={() => setConfirmOpen(false)}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
      {/* Detail: Target View */}
      {selectedTarget && (
        <DetailView
          selectedTarget={selectedTarget}
          selectedNight={selectedNight}
          setSelectedNight={setSelectedNight}
          nightKeys={nightKeys}
          setTargetLastSelectedNight={setTargetLastSelectedNight}
          setEditDialogOpen={setEditDialogOpen}
          confirmDialogRef={confirmDialogRef}
          setConfirmDialogOpen={setConfirmDialogOpen}
          imagesForView={imagesForView}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          frameTypeFilter={frameTypeFilter}
          setFrameTypeFilter={setFrameTypeFilter}
          filterTypeFilter={filterTypeFilter}
          setFilterTypeFilter={setFilterTypeFilter}
          getNightMeta={getNightMeta}
          formatMultipleValues={formatMultipleValues}
          setSelectedTarget={setSelectedTarget}
        />
      )}
    </Box>
  );
};

export default MainContentModern;
